import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { logger } from '../../../logging';
import MicOffIcon from '../../../assets/icons/MicOff';
import MicIcon from '../../../assets/icons/Mic';
import VideocamIcon from '../../../assets/icons/Videocam';
import VideocamOffIcon from '../../../assets/icons/VideocamOff';
import { useDeviceSelectors } from 'react-device-detect';
import { useLocalTracks } from '@caazam/boutiq-video-room';
import { waitingRoomMessages } from './messages';
import { VideoScreenSpinner } from './components/video-screen-preview-loader/video-screen-spinner';
import './video-screen-preview.scss';
import {useLocation} from "react-router-dom";
import {VIDEO_CALL_PAGES} from "../../VideoCall";
import {PAGES} from "../../../constants/routes";

export const VideoScreenPreview = ({ trackError, isAcquiring, smallPreview, setButtonDisabled, isBoutiqVideoSupported }) => {

    const Intl = useIntl();
    const location = useLocation();
    const videoPreviewRef = useRef(null);
    const [isVideoAttached, setIsVideoAttached] = useState(false);
    
    const {
        localVideo: videoTrack,
        localAudio: audioTrack,
        isAudioMuted,
        isVideoMuted,
        toggleAudio,
        toggleVideo,
    } = useLocalTracks();

    const [isControlsVisible, setIsControlsVisible] = useState(false);
    const [videoAttaching, setVideoAttaching] = useState(false);
    const videoElementWrapperRef = useRef(null);
    const controlsWrapperRef = useRef(null);
    const [deviceSelectors]  = useDeviceSelectors();
    const { isIOS, isAndroid, browserName } = deviceSelectors

    const localTracksCount = useMemo(()=>{
        let tracks =[];
        if (audioTrack) {
            tracks.push(audioTrack)
        }
        if (videoTrack) {
            tracks.push(videoTrack)
        }
        return tracks.length;
    },[audioTrack, videoTrack]);

    useEffect(() => {
        const isVideoPreviewDisabled = localTracksCount === 0 || isAcquiring;
        setButtonDisabled(isVideoPreviewDisabled);
        setIsControlsVisible(isBoutiqVideoSupported && !isVideoPreviewDisabled);
    }, [isAcquiring, localTracksCount]);

    useEffect(()=>{
        if (!isBoutiqVideoSupported) {
            logger.warn(`browser ${browserName} is not supported ${isIOS ? 'iOS device' : ''}${isAndroid ? 'Android device' : '' }`);
        }
    }, [isBoutiqVideoSupported, isIOS, isAndroid]);

    const renderVideoPreviewMessage = () => {
        if (!isBoutiqVideoSupported) {
            if (isIOS) {
                return (
                    <span>
                        <FormattedMessage {...waitingRoomMessages.browser_media_not_supported} values={{ browserName }} />{'. '}
                        <strong><FormattedMessage {...waitingRoomMessages.browser_media_not_supported_ios} /></strong>
                    </span>)
            } else if (isAndroid) {
                return (
                    <span>
                        <FormattedMessage {...waitingRoomMessages.browser_media_not_supported} values={{ browserName }} />{'. '}
                        <strong><FormattedMessage {...waitingRoomMessages.browser_media_not_supported_android} /></strong>
                    </span>)
            } else {
                return <FormattedMessage {...waitingRoomMessages.browser_media_not_supported}
                    values={{ browserName: Intl.formatMessage({ ...waitingRoomMessages.browser_this }) }}
                />
            }
        } else if (trackError?.error) {
            const devices = () => {
                if (trackError.camera.error && trackError.mic.error) return 'both';
                else if (trackError.camera.error) return 'camera';
                else if (trackError.mic.error) return 'mic';
            }
            if (['cam-in-use', 'mic-in-use', 'cam-mic-in-use'].includes(trackError.error.type)) {
                return Intl.formatMessage({ ...waitingRoomMessages.browser_media_abort });
            } else if (trackError.error.type === 'permissions') {
                return Intl.formatMessage({ ...waitingRoomMessages.browser_media_not_allowed }, { device: devices() });
            } else if (trackError.error.type === 'not-found') {
                return Intl.formatMessage({ ...waitingRoomMessages.browser_media_not_found }, { device: devices() });
            } else {
                return Intl.formatMessage({ ...waitingRoomMessages.browser_media_error }, { errorMsg: trackError.error.message + ':' + trackError.error.reason });
            }
        }
        else if (trackError) {
            return Intl.formatMessage({ ...waitingRoomMessages.browser_media_error }, { errorMsg: trackError.message });
        } else if (isAcquiring) {
            return <FormattedMessage {...waitingRoomMessages.acquire_media} />
        }
    }

    useEffect(() => {
        if (videoTrack) {
            setVideoAttaching(true);
            if (isVideoAttached) {
                setVideoAttaching(false);
            }
        }
    }, [videoTrack, isVideoAttached]);

    useEffect(() => {
        if (smallPreview) {
            if (videoElementWrapperRef.current) {
                const SMPreviewWidth = videoElementWrapperRef.current.clientWidth / 4;
                const controlsScale = SMPreviewWidth / 144;
                controlsWrapperRef.current.style.transform = `scale(${controlsScale})`;
                controlsWrapperRef.current.style.left = `35px`;
            }
        }

    }, [videoElementWrapperRef.current, smallPreview])

    useEffect(() => {
        if (videoPreviewRef?.current && location?.pathname === `${PAGES.VIDEO_CALL}/${VIDEO_CALL_PAGES.WAITING_ROOM}`) {
            controlsWrapperRef.current.style.marginTop = `${videoPreviewRef?.current.clientHeight / 2 + 50}px`;
        }
    }, [videoPreviewRef?.current, smallPreview])

    return (
        <div className={`video-container-wrapper${smallPreview ? ' small-preview' : ''}`}>
            <div className='video-container'>
                <div ref={videoElementWrapperRef} className='video-element-wrapper'>
                    {isBoutiqVideoSupported && !trackError && <VideoScreenSpinner isLoading={videoAttaching || isAcquiring} />}
                    {videoTrack && <VideoElement track={videoTrack} setIsVideoAttached={setIsVideoAttached} videoPreviewRef={videoPreviewRef} />}
                </div>
                {(localTracksCount === 0 || !!trackError) && !smallPreview &&
                    <span className='av-alert-message'>
                        {renderVideoPreviewMessage()}
                    </span>
                }
                <div ref={controlsWrapperRef} className={`preview-controls${isControlsVisible ? ' visible' : ''} ${location?.pathname === `${PAGES.VIDEO_CALL}/${VIDEO_CALL_PAGES.WAITING_ROOM}` && 'waiting-room_control-position'}`}>
                    {!trackError && <button onClick={toggleVideo} aria-label='mute video'>
                        <div className={`video-button-loader${videoAttaching ? ' animated' : ''}`}></div>
                        {!isVideoMuted ? (
                            <VideocamIcon size={42}/>
                        ) : (
                            <VideocamOffIcon size={42}/>
                        )}
                    </button>}
                    {audioTrack && <button onClick={toggleAudio} aria-label='mute audio'>
                        {!isAudioMuted ? (
                            <MicIcon size={42}/>
                        ) : (
                            <MicOffIcon size={42}/>
                        )}
                    </button>}
                </div>
            </div>
        </div>
    );
}


const VideoElement = ({ track, setIsVideoAttached, videoPreviewRef }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        videoPreviewRef.current = videoRef.current;
        videoRef.current.srcObject = new MediaStream([track]);
        setIsVideoAttached(true);
        return () => {
            setIsVideoAttached(false);
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };
    }, [track]);

    const isFrontFacing = track.getSettings().facingMode !== 'environment';

    return (
        <video autoPlay muted playsInline
            ref={videoRef}
            style={isFrontFacing ? { transform: 'scaleX(-1)' } : null}
        />
    );
};

